import { BlueprintInstanceType, type Island } from '@rta/rta-blueprint-library';

/** What an island abuts on a given side — the reason that side's countertop overhang was cut. */
export type OverhangReductionCause = 'island' | 'obstruction';

/**
 * Maps each abutted side (keyed by panel label: 'front' | 'left' | 'back' | 'back left' |
 * 'back right' | 'right') to what it abuts, so notes can name the CAUSE of a reduced overhang
 * instead of guessing from its value — `OVERHANG_REMOVAL_BY_MATERIAL` (island corner trim) and the
 * unconditional obstruction removal in `newConnectionCfn` both cut to the same .75", so the value
 * cannot tell a wall from a trim piece. Detected exactly like `getCleanEdgeLabels` does it.
 * An island abutment outranks an obstruction on the same side: the trim piece physically exists there.
 */
export const getOverhangReductionCauses = (island: Island): Map<string, OverhangReductionCause> => {
	const causes = new Map<string, OverhangReductionCause>();
	const connections = [...island.connections.getSourceConnections(), ...island.connections.getTargetConnections()];

	connections.forEach((connection) => {
		const { sourcePanel: panel, targetIsland: neighbour } = connection;
		const label = panel?.label ?? '';
		if (label === '' || !neighbour) return;
		if (neighbour.instanceOf === BlueprintInstanceType.Island) {
			causes.set(label, 'island');
			return;
		}
		if (neighbour.instanceOf === BlueprintInstanceType.Obstruction && causes.get(label) !== 'island') causes.set(label, 'obstruction');
	});

	return causes;
};
